import { useEffect, useState } from 'react';
import { Route, Routes, useNavigate } from 'react-router-dom';
import { getCompanyDetails } from '@lendica/api';
import { CompanyStatus } from '@lendica/utils';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';

import { AppContainer, useAppContainerContext } from './AppContainer';
import { ResetPasswordComponent } from './ResetPassword';
import { passwordCreateOrResetAuthed } from '../api';

export const CreatePasswordView = () => {
    const navigate = useNavigate();
    const [initialized, setInitialized] = useState(false);
    const [allowed, setAllowed] = useState(false);

    useEffect(() => {
        (async () => {
            try {
                const company = await getCompanyDetails();
                if (company?.status !== CompanyStatus.Lead) {
                    setAllowed(true);
                } else {
                    navigate('/application', { replace: true });
                }
            } catch {
                navigate('/application', { replace: true });
            } finally {
                setInitialized(true);
            }
        })();
    }, []);

    if (!initialized || !allowed) {
        return null;
    }

    return (
        <Routes>
            <Route path="/" element={<AppContainer />}>
                <Route
                    index
                    element={
                        <SubmittedStatusView message="Thank you for submitting the application. We’ll get back to you within 24 hours." />
                    }
                />
                <Route path="create" element={<CreatePassword />} />
                <Route
                    path="success"
                    element={
                        <SubmittedStatusView message="Password has been successfully created. You can close this page now." />
                    }
                />
            </Route>
        </Routes>
    );
};

const CreatePassword = () => {
    const { setError, setSuccessMsg } = useAppContainerContext();
    const navigate = useNavigate();

    useEffect(() => {
        setError(null);
        setSuccessMsg(
            'Thank you for submitting the application. We’ll get back to you within 24 hours.'
        );
    }, []);

    const handleSubmit = async (
        values: any,
        { setSubmitting }: { setSubmitting: (isSubmitting: boolean) => void }
    ) => {
        try {
            setSubmitting(true);
            setSuccessMsg(null);
            setError(null);
            await passwordCreateOrResetAuthed(values.password, values.confirmPassword);
            setSubmitting(false);
            navigate('/submitted', { replace: true });
        } catch (error: any) {
            setSubmitting(false);
            setSuccessMsg(null);
            setError(error.message || 'Something went wrong. Please try again.');
        }
    };

    return (
        <ResetPasswordComponent
            heading="Set your password (optional)"
            body="You can use your email and password to login to portal once you’re approved."
            action="Set password"
            handleSubmit={handleSubmit}
        />
    );
};

export const SubmittedStatusView = ({ message }: { message: string }) => {
    return (
        <Box
            sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'flex-start',
                justifyContent: 'center',
                flex: 1,
                gap: 2,
            }}
        >
            <Typography variant="h4" sx={{ fontSize: '1.125rem', fontWeight: 800 }}>
                Thank you. You're all set!
            </Typography>
            <Alert severity="success">{message}</Alert>
        </Box>
    );
};
